/**
 * =============================================================================
 * GLOBAL AUTH UTILITIES
 * =============================================================================
 *
 * Centralized authentication utilities for the entire application.
 * All components and pages should import auth functions from here.
 *
 * INCLUDES:
 * - Login/Signup API calls
 * - Merchant ID resolution (from JWT token via /auth/me)
 * - Auth token management
 * - Cache management (must clear on logout)
 *
 * NOTE: This file must NOT import from './index' to avoid circular dependencies!
 */

// =============================================================================
// API BASE URL (handles dev proxy vs production)
// =============================================================================
const DEFAULT_PROD_API_URL = 'https://api.reparo.co.in';
const normalizedProdApiUrl = (
    import.meta.env.VITE_API_BASE_URL || DEFAULT_PROD_API_URL
).replace(/\/+$/, '');

const API_BASE = import.meta.env.DEV ? '' : normalizedProdApiUrl;

// =============================================================================
// LOGIN/SIGNUP TYPES
// =============================================================================

export type LoginParams = {
    email: string;
    password: string;
    /** Set on the second attempt, after the server asked which organisation to open. */
    organizationId?: string;
};

export type SignupParams = {
    firstName: string;
    lastName: string;
    email: string;
    password: string;
    /** Optional; the server derives one from the email domain when blank. */
    organizationName?: string;
};

export type OrganizationChoice = { id: string; name: string };

/**
 * Login's other answer: the credentials opened logins in several organisations, so the
 * caller must pick one and resend with `organizationId`.
 */
export type OrganizationSelectionRequired = {
    requiresOrganizationSelection: true;
    organizations: OrganizationChoice[];
};

export const isOrganizationSelectionRequired = (
    res: AuthResponse | OrganizationSelectionRequired,
): res is OrganizationSelectionRequired =>
    (res as OrganizationSelectionRequired).requiresOrganizationSelection === true;

export type AuthResponse = {
    accessToken: {
        token: string;
        expiresIn: number;
    };
    user: {
        id: string;
        email: string;
        firstName: string;
        lastName: string;
        createdAt: string;
        updatedAt: string;
        phone: string | null;
        organizationId: string;
    };
};

export type UserDto = {
    id?: string;
    email?: string | null;
    firstName?: string | null;
    lastName?: string | null;
    organizationId?: string | null;
    phone?: string | null;
    isActive?: boolean;
    role?: 'USER' | 'ADMIN' | 'CONSULTANT' | 'VENDOR' | 'CUSTOMER';
    /** CUSTOMER logins: the B2B customer the person orders for. */
    b2bCustomerId?: string | null;
    createdAt?: string;
    updatedAt?: string;
};

// =============================================================================
// LOGIN/SIGNUP API CALLS
// =============================================================================

export const login = async (
    params: LoginParams,
): Promise<AuthResponse | OrganizationSelectionRequired> => {
    const url = `${API_BASE}/api/v1/auth/login`;
    const response = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(params),
    });

    if (!response.ok) {
        const errorData = await response.json();
        throw errorData;
    }

    const data = await response.json();
    // Handle wrapped response format
    if (
        data &&
        typeof data === 'object' &&
        'status' in data &&
        data.status === 'ok'
    ) {
        return data.results;
    }
    return data;
};

/** What the login page accepts via router state when signup sends someone over. */
export type LoginPrefill = { email?: string; organizationId?: string };

export type SignupLookupResult = {
    /** True when the address already owns at least one login. */
    existing: boolean;
    firstName?: string | null;
    lastName?: string | null;
    organizations: OrganizationChoice[];
};

/**
 * The organisations an address already has logins in (to go log into one) and the name
 * to prefill on a new one. Unknown address → `existing: false`.
 */
export const signupLookup = async (email: string): Promise<SignupLookupResult> => {
    const url = `${API_BASE}/api/v1/auth/signup/lookup`;
    const response = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
    });

    if (!response.ok) {
        const errorData = await response.json();
        throw errorData;
    }

    const data = await response.json();
    return data && typeof data === 'object' && data.status === 'ok'
        ? data.results
        : data;
};

export const signup = async (params: SignupParams): Promise<AuthResponse> => {
    const url = `${API_BASE}/api/v1/auth/signup`;
    const response = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(params),
    });

    if (!response.ok) {
        const errorData = await response.json();
        throw errorData;
    }

    const data = await response.json();
    // Handle wrapped response format
    if (
        data &&
        typeof data === 'object' &&
        'status' in data &&
        data.status === 'ok'
    ) {
        return data.results;
    }
    return data;
};

// =============================================================================
// AUTH TOKEN
// =============================================================================

/**
 * Fetches current authenticated user's profile from /auth/me
 */
export const getMe = async (): Promise<UserDto | null> => {
    const token = getAuthToken();
    if (!token) return null;

    try {
        const authUrl = `${API_BASE}/api/v1/auth/me`;
        const response = await fetch(authUrl, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${token}`,
            },
        });

        if (!response.ok) return null;

        const rawData = await response.json();
        const userData =
            rawData &&
            typeof rawData === 'object' &&
            'status' in rawData &&
            rawData.status === 'ok'
                ? rawData.results
                : rawData;
        return userData as UserDto;
    } catch {
        return null;
    }
};

/**
 * Gets JWT token from localStorage
 * Key: "token" (stored in Application > Local Storage)
 */
export const getAuthToken = (): string | null => {
    try {
        return localStorage.getItem('token');
    } catch (e) {
        console.warn('[Auth] Failed to get token from localStorage:', e);
        return null;
    }
};

// =============================================================================
// MERCHANT ID RESOLUTION
// =============================================================================

/**
 * Cached merchant ID to avoid repeated auth calls.
 * IMPORTANT: This must be cleared on logout!
 */
let cachedMerchantId: string | null = null;

/**
 * Fetches merchant ID (organizationId) from /auth/me endpoint
 * Uses JWT token from localStorage
 *
 * Flow:
 * 1. Check cache first
 * 2. Get JWT token from localStorage (key: "token")
 * 3. Call GET /api/v1/auth/me with Bearer token
 * 4. Extract organizationId from response (this is the merchant_id)
 * 5. Cache for subsequent calls
 *
 * @returns Promise<string | null> - The merchant ID or null if auth fails
 */
export const getMerchantId = async (): Promise<string | null> => {
    // Return cached value if available
    if (cachedMerchantId) {
        console.log('[Auth] Using cached merchant_id:', cachedMerchantId);
        return cachedMerchantId;
    }

    const token = getAuthToken();
    if (!token) {
        console.error('[Auth] No auth token found in localStorage');
        return null;
    }

    try {
        const authUrl = `${API_BASE}/api/v1/auth/me`;
        console.log('[Auth] Resolving merchant_id from:', authUrl);

        const response = await fetch(authUrl, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${token}`,
            },
        });

        if (!response.ok) {
            console.error(
                `[Auth] Auth failed: ${response.status} ${response.statusText}`,
            );
            return null;
        }

        const rawData = await response.json();
        // Handle wrapped response format {status: 'ok', results: {...}}
        const userData =
            rawData &&
            typeof rawData === 'object' &&
            'status' in rawData &&
            rawData.status === 'ok'
                ? rawData.results
                : rawData;
        // organizationId is the merchant_id
        cachedMerchantId = userData?.organizationId || null;

        console.log('[Auth] Resolved merchant_id:', cachedMerchantId);
        return cachedMerchantId;
    } catch (error) {
        console.error('[Auth] Error fetching merchant ID:', error);
        return null;
    }
};

/**
 * Clears cached merchant ID.
 * MUST be called on logout to ensure the next user gets their own merchant_id!
 */
export const clearMerchantIdCache = (): void => {
    cachedMerchantId = null;
    console.log('[Auth] Cleared merchant_id cache');
};

/**
 * Gets auth headers for API requests
 * Includes Bearer token if available
 */
export const getAuthHeaders = (): Record<string, string> => {
    const token = getAuthToken();
    const headers: Record<string, string> = {
        'Content-Type': 'application/json',
    };

    if (token) {
        headers.Authorization = `Bearer ${token}`;
    }

    return headers;
};

/**
 * Get the API base URL
 */
export const getApiBase = (): string => API_BASE;
